import {
  Forbidden,
  Popover,
  getReference,
  refreshReference,
  uploadReference,
  type ReferenceOverview,
  type ReferenceRun,
  type ReferenceSet,
  type ReferenceStep,
  MoreIcon,
} from '@trippath/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { FlightServices } from './FlightServices';

type State =
  | { kind: 'loading' }
  | { kind: 'forbidden' }
  | { kind: 'failed' }
  | { kind: 'ready'; overview: ReferenceOverview };

/** How long between asking how a refresh goes, while one runs. */
const POLL_MS = 1500;

/** A size in bytes as read: `8.2 MB`, `396 kB`. */
function sizeOf(bytes: number, lang: string): string {
  const [amount, unit] =
    bytes >= 1_000_000 ? [bytes / 1_000_000, 'MB'] : [Math.max(bytes / 1000, 0.1), 'kB'];
  return `${amount.toLocaleString(lang, { maximumFractionDigits: 1 })} ${unit}`;
}

/** An instant as the viewer's clock says it: `14.10.2026, 10:13`. */
function when(at: string, lang: string): string {
  return new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(at),
  );
}

/** How a refresh went, as a mark: a green tick, a red cross, a blue ring while it runs. */
function StatusMark({ status }: { status: 'ok' | 'failed' | 'running' }) {
  const { t } = useTranslation();
  const said = t(`data.status.${status}`);
  return (
    <span className={`data-mark data-mark-${status}`} role="img" aria-label={said} title={said}>
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {status === 'ok' ? (
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        ) : status === 'failed' ? (
          <path d="M6 6l12 12M18 6L6 18" />
        ) : (
          <circle cx="12" cy="12" r="6" />
        )}
      </svg>
    </span>
  );
}

/** Where a list was read from, said short: an address without its query — Wikidata's is a whole
 * query long — or a file's name as it is. */
export function sourceOf(source: string): string {
  return URL.canParse(source) ? new URL(source).host + new URL(source).pathname : source;
}

/** What can be done with a list, behind its menu: refreshed from its source, or from a file. */
function SetMenu({
  name,
  origin,
  busy,
  onRefresh,
  onFile,
}: {
  name: string;
  origin: string;
  busy: boolean;
  onRefresh: () => void;
  onFile: (file: File) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      label={t('data.actionsOf', { name })}
      trigger={(props) => (
        <button
          type="button"
          className="icon-button icon-button-small"
          aria-label={t('data.actionsOf', { name })}
          title={t('data.actionsOf', { name })}
          disabled={busy}
          {...props}
        >
          <MoreIcon />
        </button>
      )}
    >
      <div className="data-menu" role="menu">
        <button
          type="button"
          role="menuitem"
          className="data-menu-item"
          onClick={() => {
            setOpen(false);
            onRefresh();
          }}
        >
          {t('data.refresh')}
          <span className="quiet">{new URL(origin).host}</span>
        </button>
        <label className="data-menu-item" role="menuitem">
          {t('data.upload')}
          <span className="quiet">{t('data.uploadHint')}</span>
          <input
            className="visually-hidden"
            type="file"
            aria-label={t('data.uploadOf', { name })}
            onChange={(event) => {
              const file = event.target.files?.item(0);
              event.target.value = '';
              setOpen(false);
              if (file) onFile(file);
            }}
          />
        </label>
      </div>
    </Popover>
  );
}

/**
 * The reference data the instance knows — airlines, aircraft types, aircraft by registration —
 * for whoever runs it: how many of each, from where and since when; each refreshed from its
 * source or a file, on its own while this page follows its log.
 */
export function DataSources() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState<'lists' | 'services' | 'log'>('lists');

  const load = useCallback(() => {
    getReference().then(
      (overview) => {
        setState({ kind: 'ready', overview });
      },
      (error: unknown) => {
        setState({ kind: error instanceof Forbidden ? 'forbidden' : 'failed' });
      },
    );
  }, []);

  useEffect(load, [load]);

  const running = state.kind === 'ready' && state.overview.runs.some((run) => run.ok === null);
  // While a refresh runs, how it goes is asked again.
  useEffect(() => {
    if (!running) return;
    const timer = window.setTimeout(load, POLL_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [running, state, load]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p role="alert">{t(state.kind === 'forbidden' ? 'data.ownerOnly' : 'trips.failed')}</p>
        {/* Not let in: back to one's trips; not read: asked again. */}
        {state.kind === 'forbidden' ? (
          <Link to="/">{t('trips.back')}</Link>
        ) : (
          <button type="button" className="button" onClick={load}>
            {t('data.again')}
          </button>
        )}
      </div>
    );
  }
  const { overview } = state;
  const start = (job: Promise<null>) => {
    setFailed(false);
    job.then(load, () => {
      setFailed(true);
    });
  };
  const busy = (name: ReferenceSet) =>
    overview.runs.some((r) => r.name === name && r.ok === null);
  const said = (step: ReferenceStep) => {
    const count = step.count ?? 0;
    const text = step.text ?? '';
    switch (step.step) {
      case 'fetch':
        return t('data.step.fetch', { source: sourceOf(text) });
      case 'fetched':
        return t('data.step.fetched', { size: sizeOf(count, lang) });
      case 'file':
        return t('data.step.file', { name: text, size: sizeOf(count, lang) });
      case 'read':
        return t('data.step.read', { count, n: count.toLocaleString(lang) });
      case 'kept':
        return t('data.step.kept', {
          seconds: count.toLocaleString(lang, { maximumFractionDigits: 1 }),
        });
      case 'failed':
        return t('data.step.failed', {
          error: text === 'not_the_file' ? t('data.notTheFile') : text,
        });
      case 'stopped':
        return t('data.step.stopped');
    }
  };
  const status = (run: ReferenceRun) =>
    run.ok === null ? 'running' : run.ok ? 'ok' : 'failed';

  return (
    <div className="page data-page">
      <div className="page-head">
        <div>
          <h1>{t('data.title')}</h1>
          <p className="quiet">{t('data.lead')}</p>
        </div>
      </div>
      {failed && (
        <p className="form-failed" role="alert">
          {t('data.failed')}
        </p>
      )}
      <div className="segmented data-tabs" role="tablist" aria-label={t('data.title')}>
        {(['lists', 'services', 'log'] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={tab === option}
            onClick={() => {
              setTab(option);
            }}
          >
            {t(`data.tabs.${option}`)}
          </button>
        ))}
      </div>
      <section className="panel data-card" aria-labelledby="data-sets" hidden={tab !== 'lists'}>
        <h2 id="data-sets">{t('data.sets')}</h2>
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">{t('data.set')}</th>
              <th scope="col">{t('data.records')}</th>
              <th scope="col">{t('data.source')}</th>
              <th scope="col">{t('data.updated')}</th>
              <th scope="col">
                <span className="visually-hidden">{t('data.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {overview.sets.map((set) => {
              const last = overview.runs.find((run) => run.name === set.name);
              return (
                <tr key={set.name}>
                  <th scope="row" data-label={t('data.set')}>
                    <span className="data-name">{t(`data.name.${set.name}`)}</span>
                    <span className="quiet data-what">{t(`data.what.${set.name}`)}</span>
                  </th>
                  <td data-label={t('data.records')} className="mono">
                    {set.records.toLocaleString(lang)}
                  </td>
                  <td data-label={t('data.source')} className="data-source">
                    {set.source === null ? (
                      <span className="quiet">
                        {t(set.records === 0 ? 'data.notLoaded' : 'data.builtIn')}
                      </span>
                    ) : (
                      <span className="mono">{sourceOf(set.source)}</span>
                    )}
                  </td>
                  <td data-label={t('data.updated')}>
                    {set.updatedAt === null ? '—' : when(set.updatedAt, lang)}
                    {last && <StatusMark status={status(last)} />}
                  </td>
                  <td className="data-menu-cell">
                    <SetMenu
                      name={t(`data.name.${set.name}`)}
                      origin={set.origin}
                      busy={busy(set.name)}
                      onRefresh={() => {
                        start(refreshReference(set.name));
                      }}
                      onFile={(file) => {
                        start(uploadReference(set.name, file));
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <section
        className="panel data-card"
        aria-labelledby="data-services"
        hidden={tab !== 'services'}
      >
        <h2 id="data-services">{t('flightApis.title')}</h2>
        <FlightServices />
      </section>
      <section className="panel data-card" aria-labelledby="data-log" hidden={tab !== 'log'}>
        <h2 id="data-log">{t('data.log')}</h2>
        {overview.runs.length === 0 ? (
          <p className="quiet">{t('data.noRuns')}</p>
        ) : (
          <ol className="data-runs">
            {overview.runs.map((run) => (
              <li key={run.id} className="data-run">
                <p className="data-run-head">
                  <strong>{t(`data.name.${run.name}`)}</strong>
                  <span className="quiet">
                    {' · '}
                    {t(`data.way.${run.way}`)} · {when(run.startedAt, lang)}
                  </span>
                  <StatusMark status={status(run)} />
                </p>
                <ul className="data-steps mono">
                  {run.log.map((step, at) => (
                    <li
                      key={at}
                      className={step.step === 'failed' ? 'data-step-failed' : undefined}
                    >
                      {said(step)}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
